import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Check,
  Copy,
  KeyRound,
  Loader2,
  QrCode,
  RotateCcw,
} from "lucide-react";

import { Eyebrow } from "@/components/dravian/eyebrow";
import {
  auth as authApi,
  bridgeTokens as bridgeTokensApi,
  publications as publicationsApi,
  team as teamApi,
  workspaces as workspacesApi,
  type PublicationMode,
  type Publication,
  type PublicationVersion,
  type TeamRole,
  type ApiBridgeProvisioning,
  type ApiBridgeToken,
  type ApiBridgeTokenCreated,
} from "@/lib/api";
import { useWorkspaces, type WorkspaceDomain } from "@/store/workspaces";

export const Route = createFileRoute("/$workspace/settings")({
  head: () => ({ meta: [{ title: "Settings — Dravian Horizon" }] }),
  component: SettingsPage,
});

const DOMAINS: WorkspaceDomain[] = [
  "geopolitical",
  "supply_chain",
  "financial",
  "threat",
  "compliance",
  "custom",
];

function SettingsPage() {
  const { workspace: id } = useParams({ from: "/$workspace/settings" });
  const navigate = useNavigate();
  const { getById, updateWorkspace, deleteWorkspace, togglePublish } = useWorkspaces();
  const ws = getById(id);
  const [tab, setTab] = useState<
    "general" | "members" | "publishing" | "devices" | "danger"
  >("general");

  return (
    <div className="p-5 max-w-[900px] mx-auto">
      <div className="label-eyebrow mb-1">Settings</div>
      <h2 className="text-[20px] font-semibold mb-5">Workspace Settings</h2>
      <div className="flex gap-1 border-b border-border mb-5">
        {(["general", "members", "publishing", "devices", "danger"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`relative px-4 py-2.5 text-[11px] uppercase tracking-wider font-semibold ${
              tab === t ? "text-foreground" : "text-muted-foreground"
            }`}
          >
            {t === "danger" ? "Danger Zone" : t.charAt(0).toUpperCase() + t.slice(1)}
            {tab === t && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-primary" />}
          </button>
        ))}
      </div>

      {tab === "general" && ws && <GeneralTab workspaceId={id} initial={ws} onSave={updateWorkspace} />}
      {tab === "publishing" && <PublishingTab workspaceId={id} />}
      {tab === "members" && <MembersTab workspaceId={id} />}
      {tab === "devices" && <DevicesTab workspaceId={id} />}
      {tab === "danger" && (
        <DangerTab
          workspaceName={ws?.name ?? "this workspace"}
          onDelete={async () => {
            await deleteWorkspace(id);
            navigate({ to: "/hq" });
          }}
        />
      )}
    </div>
  );
}

interface GeneralForm {
  name: string;
  description: string;
  domain: WorkspaceDomain;
}

function GeneralTab({
  workspaceId,
  initial,
  onSave,
}: {
  workspaceId: string;
  initial: { name: string; description: string; domain: WorkspaceDomain };
  onSave: (
    id: string,
    patch: { name?: string; description?: string; domain?: WorkspaceDomain },
  ) => Promise<void>;
}) {
  const baseline: GeneralForm = useMemo(
    () => ({
      name: initial.name,
      description: initial.description ?? "",
      domain: initial.domain,
    }),
    [initial.name, initial.description, initial.domain],
  );
  const [form, setForm] = useState<GeneralForm>(baseline);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Reset local form when the workspace data refreshes externally
  useEffect(() => {
    setForm(baseline);
  }, [baseline]);

  const dirty = useMemo(
    () =>
      form.name.trim() !== baseline.name ||
      form.description !== baseline.description ||
      form.domain !== baseline.domain,
    [form, baseline],
  );

  const onChange = <K extends keyof GeneralForm>(key: K, value: GeneralForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    if (!dirty || saving) return;
    if (!form.name.trim()) {
      setError("Name cannot be empty.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const patch: { name?: string; description?: string; domain?: WorkspaceDomain } = {};
      if (form.name.trim() !== baseline.name) patch.name = form.name.trim();
      if (form.description !== baseline.description) patch.description = form.description;
      if (form.domain !== baseline.domain) patch.domain = form.domain;
      await onSave(workspaceId, patch);
      setSavedAt(Date.now());
      // Clear the saved-confirmation badge after a moment
      setTimeout(() => setSavedAt(null), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setForm(baseline);
    setError(null);
  };

  return (
    <div className="space-y-4">
      <div className="panel p-5 space-y-4">
        <Field label="Name">
          <input
            value={form.name}
            onChange={(e) => onChange("name", e.target.value)}
            className="w-full bg-surface-elevated border border-border rounded-md px-3 py-2 text-[13px] outline-none focus:border-primary/50"
          />
        </Field>
        <Field label="Description">
          <textarea
            value={form.description}
            onChange={(e) => onChange("description", e.target.value)}
            rows={4}
            className="w-full bg-surface-elevated border border-border rounded-md px-3 py-2 text-[13px] outline-none focus:border-primary/50 resize-none"
            placeholder="What does this workspace track?"
          />
        </Field>
        <Field label="Domain">
          <div className="flex flex-wrap gap-1.5">
            {DOMAINS.map((d) => {
              const active = form.domain === d;
              return (
                <button
                  key={d}
                  onClick={() => onChange("domain", d)}
                  className={`rounded-md border px-3 py-1.5 text-[12px] transition ${
                    active
                      ? "border-primary/50 bg-primary/10 text-foreground"
                      : "border-border bg-surface text-foreground/80 hover:bg-surface-elevated"
                  }`}
                >
                  {d.replace("_", " ")}
                </button>
              );
            })}
          </div>
        </Field>
      </div>

      {/* Save bar — sticky, only visible when dirty (or just after save) */}
      {(dirty || savedAt) && (
        <div className="sticky bottom-3 z-30 flex items-center justify-between rounded-md border border-primary/40 bg-surface/95 backdrop-blur px-4 py-3 shadow-xl">
          <div className="flex items-center gap-3 text-[12px]">
            {dirty && (
              <span className="inline-flex items-center gap-1.5 text-foreground/90">
                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                Unsaved changes
              </span>
            )}
            {!dirty && savedAt && (
              <span className="inline-flex items-center gap-1.5 text-risk-low">
                <Check className="h-3.5 w-3.5" /> Saved
              </span>
            )}
            {error && (
              <span className="inline-flex items-center gap-1.5 text-risk-high">
                <AlertTriangle className="h-3.5 w-3.5" /> {error}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {dirty && (
              <button
                onClick={discard}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-[12px] text-foreground/80 hover:bg-surface-elevated disabled:opacity-50"
              >
                <RotateCcw className="h-3 w-3" /> Discard
              </button>
            )}
            <button
              onClick={save}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-[12px] font-semibold disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Publishing (EPIC-13) ----------

function PublishingTab({ workspaceId }: { workspaceId: string }) {
  const qc = useQueryClient();
  const publicationsQ = useQuery({
    queryKey: ["publications", workspaceId],
    queryFn: () => publicationsApi.list(workspaceId),
  });
  const meQ = useQuery({ queryKey: ["me"], queryFn: () => authApi.me() });
  const [creating, setCreating] = useState(false);
  const [openPub, setOpenPub] = useState<string | null>(null);

  const role = meQ.data?.role ?? "viewer";
  const canPublish = ["owner", "admin", "publisher"].includes(role);
  const isOrgAdmin = ["owner", "admin"].includes(role);

  return (
    <div className="space-y-4">
      <div className="panel p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Eyebrow>Publishing</Eyebrow>
            <p className="text-[12px] text-muted-foreground mt-1 max-w-[540px]">
              Generate sanitized snapshots of this workspace and expose them to
              chosen audiences. Drafts review before any version goes live.
              Public mode requires an organization owner or admin to activate.
            </p>
          </div>
          {canPublish && (
            <button
              onClick={() => setCreating(true)}
              className="rounded-md bg-primary text-primary-foreground px-3.5 py-2 text-[12px] font-medium"
            >
              + New publication
            </button>
          )}
        </div>
      </div>

      {publicationsQ.isLoading && (
        <div className="panel p-5 text-[12px] text-muted-foreground">Loading…</div>
      )}
      {publicationsQ.data && publicationsQ.data.length === 0 && !creating && (
        <div className="panel p-5 text-[12px] text-muted-foreground">
          No publications yet. {canPublish ? "Create one to share a sanitized view of this workspace." : "Your role can't create publications."}
        </div>
      )}

      {(publicationsQ.data ?? []).map((pub) => (
        <PublicationCard
          key={pub.id}
          publication={pub}
          workspaceId={workspaceId}
          isOpen={openPub === pub.id}
          onToggle={() => setOpenPub(openPub === pub.id ? null : pub.id)}
          canPublish={canPublish}
          isOrgAdmin={isOrgAdmin}
          onChange={() => qc.invalidateQueries({ queryKey: ["publications", workspaceId] })}
        />
      ))}

      {creating && (
        <CreatePublicationDialog
          workspaceId={workspaceId}
          isOrgAdmin={isOrgAdmin}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ["publications", workspaceId] });
          }}
        />
      )}
    </div>
  );
}

const MODE_LABELS: Record<PublicationMode, string> = {
  private: "Private",
  shared_with_user: "Shared with user",
  org_shared: "Organization shared",
  link_token: "Link snapshot",
  public: "Public snapshot",
};

const MODE_TONE: Record<PublicationMode, string> = {
  private: "bg-neutral-500/10 text-neutral-400 border-neutral-500/30",
  shared_with_user: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  org_shared: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  link_token: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  public: "bg-rose-500/10 text-rose-400 border-rose-500/30",
};

function ModeBadge({ mode }: { mode: PublicationMode }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${MODE_TONE[mode]}`}>
      {MODE_LABELS[mode]}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    generating: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    draft: "bg-neutral-500/10 text-neutral-400 border-neutral-500/30",
    active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    revoked: "bg-rose-500/10 text-rose-400 border-rose-500/30",
    archived: "bg-neutral-500/10 text-neutral-500 border-neutral-500/30",
    failed: "bg-rose-500/10 text-rose-400 border-rose-500/30",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${map[status] ?? map.draft}`}>
      {status === "generating" && (
        <span className="inline-block h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
      )}
      {status}
    </span>
  );
}

function PublicationCard({
  publication,
  workspaceId,
  isOpen,
  onToggle,
  canPublish,
  isOrgAdmin,
  onChange,
}: {
  publication: Publication;
  workspaceId: string;
  isOpen: boolean;
  onToggle: () => void;
  canPublish: boolean;
  isOrgAdmin: boolean;
  onChange: () => void;
}) {
  const qc = useQueryClient();
  const versionsQ = useQuery({
    queryKey: ["publication-versions", publication.id],
    queryFn: () => publicationsApi.listVersions(workspaceId, publication.id),
    enabled: isOpen,
    refetchInterval: (q) => {
      const list = q.state.data as PublicationVersion[] | undefined;
      const hasGenerating = list?.some((v) => v.status === "generating");
      return hasGenerating ? 5000 : false;
    },
  });
  const [previewVersion, setPreviewVersion] = useState<PublicationVersion | null>(null);
  const [activateVersion, setActivateVersion] = useState<PublicationVersion | null>(null);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const generate = useMutation({
    mutationFn: () => publicationsApi.generateVersion(workspaceId, publication.id),
    onMutate: () => setGenerateError(null),
    onSuccess: () => {
      // The endpoint returns immediately with status='generating'. The
      // versions query starts polling automatically while any version is
      // in that state.
      qc.invalidateQueries({ queryKey: ["publication-versions", publication.id] });
      onChange();
    },
    onError: (e: any) => setGenerateError(String(e?.message ?? e)),
  });

  const rollback = useMutation({
    mutationFn: (versionId: string) =>
      publicationsApi.rollbackVersion(workspaceId, publication.id, versionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["publication-versions", publication.id] });
      onChange();
    },
  });

  const versions = versionsQ.data ?? [];
  const sortedVersions = [...versions].sort((a, b) => b.version_number - a.version_number);
  const activeVersion = versions.find((v) => v.status === "active");
  const previousArchived = sortedVersions.find(
    (v) => v.status === "archived" && v.version_number !== activeVersion?.version_number,
  );
  const anyGenerating = versions.some((v) => v.status === "generating");

  const isRevoked = publication.status === "revoked";

  return (
    <div className="panel">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-surface-elevated/40 transition rounded-md"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className={`grid place-items-center w-7 h-7 rounded-md text-[12px] transition ${isOpen ? "bg-primary/15 text-primary" : "bg-surface-elevated text-muted-foreground"}`}>
            {isOpen ? "−" : "+"}
          </span>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold truncate flex items-center gap-2">
              {publication.title}
              {publication.hostname && (
                <span className="text-[11px] text-muted-foreground font-mono">
                  {publication.hostname}
                </span>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2">
              <ModeBadge mode={publication.mode} />
              <StatusBadge status={publication.status} />
              <span>
                v{activeVersion?.version_number ?? "—"} active
              </span>
            </div>
          </div>
        </div>
        <div className="text-[11px] text-muted-foreground">
          {versionsQ.data ? `${versions.length} version${versions.length === 1 ? "" : "s"}` : ""}
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-border p-5 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            {canPublish && !isRevoked && (
              <button
                onClick={() => generate.mutate()}
                disabled={generate.isPending}
                className="rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-[12px] font-medium disabled:opacity-50"
              >
                {generate.isPending ? "Queuing…" : "Generate draft snapshot"}
              </button>
            )}
            {isOrgAdmin && !isRevoked && (
              <button
                onClick={() => setRevokeOpen(true)}
                className="rounded-md border border-rose-500/40 text-rose-400 px-3 py-1.5 text-[12px] hover:bg-rose-500/10"
              >
                Revoke publication
              </button>
            )}
            {isRevoked && (
              <div className="text-[12px] text-rose-400">
                Publication revoked {publication.revoked_at?.slice(0, 10)}. The
                hostname stops serving immediately; historical versions stay on
                disk.
              </div>
            )}
          </div>

          {anyGenerating && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-300 flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
              A draft is being generated in the background. This can take 1-2
              minutes when guided Q&A is enabled. You can close this tab — the
              new version will land here when it's ready.
            </div>
          )}
          {generateError && (
            <div className="rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-[11px] text-rose-300">
              {generateError}
            </div>
          )}

          {versionsQ.isLoading ? (
            <div className="text-[12px] text-muted-foreground">Loading versions…</div>
          ) : versions.length === 0 ? (
            <div className="text-[12px] text-muted-foreground">
              No versions yet. Generate the first draft to preview and publish.
            </div>
          ) : (
            <div className="space-y-1.5">
              {sortedVersions.map((v) => (
                <div
                  key={v.id}
                  className="grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-md border border-border bg-surface-elevated/30 px-3 py-2"
                >
                  <div className="text-[12px] font-semibold w-[40px]">v{v.version_number}</div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                    <StatusBadge status={v.status} />
                    <span>{new Date(v.generated_at).toLocaleString()}</span>
                    {v.snapshot_bytes && <span>{Math.round(v.snapshot_bytes / 1024)} KB</span>}
                    {v.error && <span className="text-rose-400 truncate max-w-[300px]">{v.error}</span>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {v.status === "generating" && (
                      <span className="text-[11px] text-amber-400 italic">running…</span>
                    )}
                    {v.status !== "failed" && v.status !== "generating" && (
                      <button
                        onClick={() => setPreviewVersion(v)}
                        className="rounded-md border border-border bg-surface-elevated px-2 py-1 text-[11px] hover:border-primary/40"
                      >
                        Preview
                      </button>
                    )}
                    {canPublish && !isRevoked && v.status === "draft" && (
                      <button
                        onClick={() => setActivateVersion(v)}
                        className="rounded-md bg-primary text-primary-foreground px-2.5 py-1 text-[11px] font-medium"
                      >
                        Publish
                      </button>
                    )}
                    {canPublish &&
                      !isRevoked &&
                      v.status === "archived" &&
                      previousArchived?.id === v.id && (
                        <button
                          onClick={() => rollback.mutate(v.id)}
                          className="rounded-md border border-border bg-surface-elevated px-2.5 py-1 text-[11px]"
                        >
                          Rollback to v{v.version_number}
                        </button>
                      )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {previewVersion && (
        <SnapshotPreviewDialog
          workspaceId={workspaceId}
          publication={publication}
          version={previewVersion}
          onClose={() => setPreviewVersion(null)}
        />
      )}
      {activateVersion && (
        <ActivateDialog
          workspaceId={workspaceId}
          publication={publication}
          version={activateVersion}
          isOrgAdmin={isOrgAdmin}
          onClose={() => setActivateVersion(null)}
          onActivated={() => {
            setActivateVersion(null);
            qc.invalidateQueries({ queryKey: ["publication-versions", publication.id] });
            onChange();
          }}
        />
      )}
      {revokeOpen && (
        <RevokeDialog
          workspaceId={workspaceId}
          publication={publication}
          onClose={() => setRevokeOpen(false)}
          onRevoked={() => {
            setRevokeOpen(false);
            qc.invalidateQueries({ queryKey: ["publication-versions", publication.id] });
            onChange();
          }}
        />
      )}
    </div>
  );
}

// ---------- Create publication dialog ----------

const HOSTNAME_RE = /^[a-z0-9.-]+\.[a-z]{2,}$/i;

function CreatePublicationDialog({
  workspaceId,
  isOrgAdmin,
  onClose,
  onCreated,
}: {
  workspaceId: string;
  isOrgAdmin: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [mode, setMode] = useState<PublicationMode>("link_token");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [hostname, setHostname] = useState("");
  const [includeGeo, setIncludeGeo] = useState(false);
  const [includeScenarios, setIncludeScenarios] = useState(false);
  const [includeOntology, setIncludeOntology] = useState(false);
  const [includeQA, setIncludeQA] = useState(true);
  const [qaCount, setQaCount] = useState(8);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      publicationsApi.create(workspaceId, {
        mode,
        title: title.trim(),
        description: description.trim() || null,
        hostname: mode === "public" ? hostname.trim() : null,
        include_geo: includeGeo,
        include_scenarios: includeScenarios,
        include_ontology: includeOntology,
        include_guided_questions: includeQA,
        guided_questions_count: qaCount,
      }),
    onSuccess: onCreated,
    onError: (e: any) => setError(String(e?.message ?? e)),
  });

  const hostnameValid = mode !== "public" || HOSTNAME_RE.test(hostname.trim());
  const canSubmit = title.trim().length > 0 && hostnameValid;
  const publicAllowed = mode !== "public" || isOrgAdmin;

  return (
    <Modal onClose={onClose} title="New publication">
      <div className="space-y-4">
        <Field label="Mode">
          <div className="grid sm:grid-cols-2 gap-2">
            {(["org_shared", "link_token", "public"] as PublicationMode[]).map((m) => {
              const disabled = m === "public" && !isOrgAdmin;
              const active = mode === m;
              return (
                <button
                  key={m}
                  type="button"
                  disabled={disabled}
                  onClick={() => setMode(m)}
                  className={`text-left rounded-md border px-3 py-2.5 text-[12px] transition ${
                    active
                      ? "border-primary/50 bg-primary/10"
                      : disabled
                      ? "border-border bg-surface-elevated/30 opacity-50 cursor-not-allowed"
                      : "border-border hover:border-primary/30 bg-surface-elevated/40"
                  }`}
                >
                  <div className="font-semibold">{MODE_LABELS[m]}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    {m === "org_shared" && "Live, authenticated, all org users see this workspace read-only."}
                    {m === "link_token" && "Static snapshot. Shareable link with TTL token."}
                    {m === "public" && (disabled ? "Requires owner/admin." : "Static snapshot on a public hostname.")}
                  </div>
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Title">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Colombia Conflict Observatory — 2026 Q2"
            className="w-full bg-surface-elevated border border-border rounded-md px-3 py-2 text-[13px] outline-none focus:border-primary/50"
          />
        </Field>

        <Field label="Description (optional)">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full bg-surface-elevated border border-border rounded-md px-3 py-2 text-[12px] outline-none focus:border-primary/50 resize-none"
          />
        </Field>

        {mode === "public" && (
          <Field label="Public hostname">
            <input
              value={hostname}
              onChange={(e) => setHostname(e.target.value.toLowerCase())}
              placeholder="conflictoarmado.example.gov"
              className={`w-full bg-surface-elevated border rounded-md px-3 py-2 text-[13px] outline-none ${
                hostname && !hostnameValid ? "border-rose-500/50" : "border-border focus:border-primary/50"
              }`}
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              Configure DNS to point this hostname at your reverse proxy → horizon-public:80.
              No scheme, no path, no port.
            </div>
            {hostname && !hostnameValid && (
              <div className="text-[10px] text-rose-400 mt-1">
                Invalid hostname. Use a clean domain like `site.example.gov`.
              </div>
            )}
          </Field>
        )}

        <details className="rounded-md border border-border p-3 bg-surface-elevated/30">
          <summary className="text-[11px] text-muted-foreground cursor-pointer">Scope · what to include</summary>
          <div className="mt-3 space-y-2 text-[12px]">
            <Switch label="Geographic view (heatmap)" checked={includeGeo} onChange={setIncludeGeo} />
            <Switch label="Scenarios (narrative only)" checked={includeScenarios} onChange={setIncludeScenarios} />
            <Switch label="Ontology (types only)" checked={includeOntology} onChange={setIncludeOntology} />
            <Switch label="Guided Q&A" checked={includeQA} onChange={setIncludeQA} />
            {includeQA && (
              <div className="flex items-center gap-2 ml-6">
                <span className="text-[11px] text-muted-foreground">How many:</span>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={qaCount}
                  onChange={(e) => setQaCount(Math.max(1, Math.min(20, parseInt(e.target.value) || 8)))}
                  className="w-16 bg-surface-elevated border border-border rounded px-2 py-1 text-[11px] text-center"
                />
                <span className="text-[10px] text-muted-foreground">max 20</span>
              </div>
            )}
          </div>
        </details>

        {error && (
          <div className="rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-[11px] text-rose-300">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="rounded-md px-3 py-1.5 text-[12px] text-muted-foreground">
            Cancel
          </button>
          <button
            onClick={() => create.mutate()}
            disabled={!canSubmit || !publicAllowed || create.isPending}
            className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-[12px] font-medium disabled:opacity-50"
          >
            Create publication
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------- Snapshot preview dialog ----------

type PreviewTab = "overview" | "qa" | "counts" | "advanced";

function SnapshotPreviewDialog({
  workspaceId,
  publication,
  version,
  onClose,
}: {
  workspaceId: string;
  publication: Publication;
  version: PublicationVersion;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<PreviewTab>("overview");
  const snapshotQ = useQuery({
    queryKey: ["snapshot", publication.id, version.id],
    queryFn: () => publicationsApi.getSnapshot(workspaceId, publication.id, version.id),
    enabled: version.status !== "failed",
  });
  const snap: any = snapshotQ.data;

  return (
    <Modal onClose={onClose} title={`Preview · v${version.version_number}`} wide>
      <div className="flex items-center gap-1 border-b border-border mb-4 -mt-2">
        {(["overview", "qa", "counts", "advanced"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`relative px-3 py-2 text-[11px] uppercase tracking-wider font-semibold ${tab === t ? "text-foreground" : "text-muted-foreground"}`}
          >
            {t === "qa" ? "Guided Q&A" : t === "advanced" ? "Raw JSON" : t.charAt(0).toUpperCase() + t.slice(1)}
            {tab === t && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-primary" />}
          </button>
        ))}
      </div>

      {snapshotQ.isLoading && <div className="text-[12px] text-muted-foreground">Loading snapshot…</div>}
      {snapshotQ.isError && <div className="text-[12px] text-rose-400">Failed to load: {String((snapshotQ.error as any)?.message ?? snapshotQ.error)}</div>}

      {snap && tab === "overview" && (
        <div className="space-y-3 text-[12px]">
          <div className="grid grid-cols-2 gap-3">
            <Info label="Title" value={snap.metadata?.title} />
            <Info label="Version" value={`v${snap.metadata?.version_number}`} />
            <Info label="Generated" value={snap.metadata?.generated_at?.slice(0, 16).replace("T", " ")} />
            <Info label="Checksum" value={snap.metadata?.checksum?.slice(0, 16) + "…"} mono />
          </div>
          <Info label="Description" value={snap.metadata?.description ?? "(none)"} />
        </div>
      )}

      {snap && tab === "qa" && (
        <div className="space-y-4">
          {version.strip_counters && (
            <div className="rounded-md border border-border bg-surface-elevated/30 p-3 text-[10px] text-muted-foreground font-mono flex flex-wrap gap-x-4 gap-y-1">
              {version.strip_counters.guided_qa_attempts !== undefined && (
                <span>attempts: <span className="text-foreground">{version.strip_counters.guided_qa_attempts}</span></span>
              )}
              {version.strip_counters.guided_qa_duration_ms !== undefined && (
                <span>duration: <span className="text-foreground">{Math.round(Number(version.strip_counters.guided_qa_duration_ms) / 1000)}s</span></span>
              )}
              {version.strip_counters.guided_qa_output_tokens !== undefined && (
                <span>output tokens: <span className="text-foreground">{version.strip_counters.guided_qa_output_tokens}</span></span>
              )}
              {version.strip_counters.guided_qa_model && (
                <span>model: <span className="text-foreground">{String(version.strip_counters.guided_qa_model)}</span></span>
              )}
              {version.strip_counters.guided_questions_rejected !== undefined && (
                <span>rejected: <span className="text-foreground">{version.strip_counters.guided_questions_rejected}</span></span>
              )}
            </div>
          )}
          {(snap.guided_questions ?? []).length === 0 ? (
            <div className="text-[12px] text-muted-foreground italic">
              No guided Q&A in this snapshot. {version.error ? `(${version.error})` : ""}
            </div>
          ) : (
            (snap.guided_questions ?? []).map((q: any, i: number) => (
              <div key={q.id} className="rounded-md border border-border bg-surface-elevated/30 p-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="text-[12px] font-semibold">Q{i + 1}: {q.question}</div>
                  {q.severity && <StatusBadge status={q.severity} />}
                </div>
                <div className="text-[12px] text-muted-foreground leading-relaxed whitespace-pre-wrap">{q.answer}</div>
                <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-muted-foreground">
                  {q.related_entity_ids?.length > 0 && <span>{q.related_entity_ids.length} entities</span>}
                  {q.related_signal_ids?.length > 0 && <span>{q.related_signal_ids.length} signals</span>}
                  {q.citations?.length > 0 && (
                    <span>
                      Sources: {q.citations.map((c: any) => c.source_domain).slice(0, 3).join(", ")}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {snap && tab === "counts" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-[12px]">
          <Count label="Entities" n={snap.entities?.length} />
          <Count label="Relationships" n={snap.relationships?.length} />
          <Count label="Signals" n={snap.signals?.length} />
          <Count label="Scenarios" n={snap.scenarios?.length} />
          <Count label="Guided Q&A" n={snap.guided_questions?.length} />
          <Count label="Citation domains" n={
            new Set((snap.signals ?? []).flatMap((s: any) => s.citations?.map((c: any) => c.source_domain) ?? [])).size
          } />
        </div>
      )}

      {snap && tab === "advanced" && (
        <pre className="text-[10px] text-muted-foreground bg-background border border-border rounded-md p-3 overflow-auto max-h-[400px] font-mono">
          {JSON.stringify(snap, null, 2)}
        </pre>
      )}

      <div className="flex justify-end pt-4">
        <button onClick={onClose} className="rounded-md border border-border bg-surface-elevated px-4 py-1.5 text-[12px]">
          Close
        </button>
      </div>
    </Modal>
  );
}

// ---------- Activate / Revoke dialogs ----------

function ActivateDialog({
  workspaceId,
  publication,
  version,
  isOrgAdmin,
  onClose,
  onActivated,
}: {
  workspaceId: string;
  publication: Publication;
  version: PublicationVersion;
  isOrgAdmin: boolean;
  onClose: () => void;
  onActivated: () => void;
}) {
  const isPublic = publication.mode === "public";
  const [ack1, setAck1] = useState(false);
  const [ack2, setAck2] = useState(false);
  const [ack3, setAck3] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const publish = useMutation({
    mutationFn: () => publicationsApi.publishVersion(workspaceId, publication.id, version.id),
    onSuccess: onActivated,
    onError: (e: any) => setError(String(e?.message ?? e)),
  });

  const canProceed =
    !isPublic || (isOrgAdmin && ack1 && ack2 && ack3);

  return (
    <Modal onClose={onClose} title={isPublic ? "Activate public snapshot" : `Publish v${version.version_number}`}>
      <div className="space-y-4 text-[12px]">
        {!isPublic ? (
          <div className="text-muted-foreground">
            Activate <strong>v{version.version_number}</strong> as the current
            snapshot for this <ModeBadge mode={publication.mode} />. Previously
            active versions will be archived but kept on disk for rollback.
          </div>
        ) : (
          <>
            <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-rose-300">
              You're about to publish <strong>v{version.version_number}</strong> to
              the public internet at{" "}
              <span className="font-mono">{publication.hostname}</span>. Anyone with
              the hostname will be able to view the sanitized snapshot.
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" className="accent-primary mt-0.5" checked={ack1} onChange={(e) => setAck1(e.target.checked)} />
              <span>I have reviewed the snapshot preview and confirm nothing in it should remain private.</span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" className="accent-primary mt-0.5" checked={ack2} onChange={(e) => setAck2(e.target.checked)} />
              <span>DNS for {publication.hostname} is configured to point at our reverse proxy with TLS.</span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" className="accent-primary mt-0.5" checked={ack3} onChange={(e) => setAck3(e.target.checked)} />
              <span>The reverse proxy routes {publication.hostname} to the horizon-public container.</span>
            </label>
          </>
        )}

        {error && (
          <div className="rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-rose-300">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="rounded-md px-3 py-1.5 text-muted-foreground">
            Cancel
          </button>
          <button
            disabled={!canProceed || publish.isPending}
            onClick={() => publish.mutate()}
            className={`rounded-md px-4 py-1.5 font-medium disabled:opacity-50 ${
              isPublic
                ? "bg-rose-500 text-white"
                : "bg-primary text-primary-foreground"
            }`}
          >
            {publish.isPending
              ? "Publishing…"
              : isPublic
              ? "Publish to public hostname"
              : "Publish version"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RevokeDialog({
  workspaceId,
  publication,
  onClose,
  onRevoked,
}: {
  workspaceId: string;
  publication: Publication;
  onClose: () => void;
  onRevoked: () => void;
}) {
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const revoke = useMutation({
    mutationFn: () => publicationsApi.revoke(workspaceId, publication.id),
    onSuccess: onRevoked,
    onError: (e: any) => setError(String(e?.message ?? e)),
  });
  return (
    <Modal onClose={onClose} title="Revoke publication">
      <div className="space-y-4 text-[12px]">
        <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-rose-300">
          Revoking <strong>{publication.title}</strong> immediately removes the
          current snapshot from the public runtime. Historical versions remain
          on disk and can be re-published manually.
        </div>
        <div>
          Type <span className="font-mono text-foreground">revoke</span> to confirm:
        </div>
        <input
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          className="w-full bg-surface-elevated border border-border rounded-md px-3 py-2 text-[13px] outline-none focus:border-primary/50"
        />
        {error && <div className="rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-rose-300">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="rounded-md px-3 py-1.5 text-muted-foreground">
            Cancel
          </button>
          <button
            disabled={typed !== "revoke" || revoke.isPending}
            onClick={() => revoke.mutate()}
            className="rounded-md bg-rose-500 text-white px-4 py-1.5 font-medium disabled:opacity-50"
          >
            {revoke.isPending ? "Revoking…" : "Revoke"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------- Small primitives ----------

function Modal({
  title,
  onClose,
  wide,
  children,
}: {
  title: string;
  onClose: () => void;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-background/80 grid place-items-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className={`panel p-5 max-h-[90vh] overflow-auto w-full ${wide ? "max-w-[720px]" : "max-w-[480px]"}`}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[15px] font-semibold">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-[14px]">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Switch({ label, checked, onChange }: { label: string; checked: boolean; onChange: (b: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-primary" />
      <span>{label}</span>
    </label>
  );
}

function Info({ label, value, mono }: { label: string; value?: string; mono?: boolean }) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className={`text-[12px] ${mono ? "font-mono text-muted-foreground" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function Count({ label, n }: { label: string; n?: number }) {
  return (
    <div className="panel-elevated p-3 text-center">
      <div className="text-[22px] font-semibold">{n ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

const MEMBER_ROLES: TeamRole[] = ["admin", "supervisor", "operator", "analyst", "publisher", "viewer"];

function MembersTab({ workspaceId }: { workspaceId: string }) {
  const qc = useQueryClient();
  const membersQ = useQuery({
    queryKey: ["workspace-members", workspaceId],
    queryFn: () => teamApi.workspaceMembers(workspaceId),
  });
  const teamQ = useQuery({ queryKey: ["team"], queryFn: () => teamApi.summary() });
  const [selectedUser, setSelectedUser] = useState("");
  const [role, setRole] = useState<TeamRole>("analyst");
  const setMember = useMutation({
    mutationFn: () => teamApi.setWorkspaceMember(workspaceId, selectedUser, role),
    onSuccess: () => {
      setSelectedUser("");
      qc.invalidateQueries({ queryKey: ["workspace-members", workspaceId] });
      qc.invalidateQueries({ queryKey: ["team"] });
    },
  });
  const removeMember = useMutation({
    mutationFn: (userId: string) => teamApi.removeWorkspaceMember(workspaceId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspace-members", workspaceId] });
      qc.invalidateQueries({ queryKey: ["team"] });
    },
  });
  const currentIds = new Set((membersQ.data ?? []).map((m) => m.user_id));
  const available = (teamQ.data?.members ?? []).filter((m) => !currentIds.has(m.id));

  return (
    <div className="space-y-4">
      <OrgSharingSection workspaceId={workspaceId} />
      <div className="panel p-5 space-y-4">
        <Eyebrow count={membersQ.data?.length ?? 0}>Members</Eyebrow>
      <div className="grid md:grid-cols-[1fr_160px_auto] gap-2">
        <select
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
          className="rounded-md border border-border bg-surface px-3 py-2 text-[12px]"
        >
          <option value="">Add a local user...</option>
          {available.map((m) => (
            <option key={m.id} value={m.id}>{m.email}</option>
          ))}
        </select>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as TeamRole)}
          className="rounded-md border border-border bg-surface px-3 py-2 text-[12px]"
        >
          {MEMBER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button
          onClick={() => setMember.mutate()}
          disabled={!selectedUser || setMember.isPending}
          className="rounded-md bg-primary text-primary-foreground px-3 py-2 text-[12px] font-semibold disabled:opacity-50"
        >
          Add member
        </button>
      </div>
      {setMember.isError && <p className="text-[11px] text-risk-high">{String(setMember.error)}</p>}
      <div className="divide-y divide-border">
        {(membersQ.data ?? []).map((member) => (
          <div key={member.user_id} className="flex items-center justify-between py-2.5 gap-3">
            <div className="min-w-0">
              <div className="text-[13px] truncate">{member.email}</div>
              <div className="text-[10px] text-muted-foreground font-mono">
                last seen {new Date(member.last_seen_at).toLocaleDateString()}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider rounded-full border border-border bg-surface px-2 py-0.5">{member.role}</span>
              <button
                onClick={() => removeMember.mutate(member.user_id)}
                disabled={removeMember.isPending}
                className="rounded-md border border-border bg-surface px-2 py-1 text-[11px] text-muted-foreground hover:text-risk-high"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        {!membersQ.isLoading && (membersQ.data ?? []).length === 0 && (
          <p className="py-4 text-[12px] text-muted-foreground">
            No explicit members yet. Org owners and admins can still administer this workspace.
          </p>
        )}
        {available.length === 0 && (
          <p className="pt-2 text-[11px] text-muted-foreground">
            Users appear here after their first login to the shared deployment.
          </p>
        )}
        </div>
      </div>
    </div>
  );
}

function OrgSharingSection({ workspaceId }: { workspaceId: string }) {
  const qc = useQueryClient();
  const wsQ = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => workspacesApi.get(workspaceId),
  });
  const [error, setError] = useState<string | null>(null);
  const update = useMutation({
    mutationFn: (payload: { org_shared?: boolean; org_shared_default_role?: "viewer" | "analyst" }) =>
      workspacesApi.updateSharing(workspaceId, payload),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["workspace", workspaceId] });
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      qc.invalidateQueries({ queryKey: ["publications", workspaceId] });
    },
    onError: (e: any) => setError(String(e?.message ?? e)),
  });

  const ws = wsQ.data;
  const canManage = ws?.effective_role === "owner" || ws?.effective_role === "admin";
  const enabled = !!ws?.org_shared;
  const defaultRole = (ws?.org_shared_default_role ?? "viewer") as "viewer" | "analyst";

  return (
    <div className="panel p-5 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Eyebrow>Organization sharing</Eyebrow>
          <p className="text-[12px] text-muted-foreground mt-1 max-w-[520px]">
            Org sharing gives everyone in this organization live read access by
            default. Explicit members override this default.
          </p>
        </div>
        <label className="flex items-center gap-2 text-[12px] cursor-pointer">
          <input
            type="checkbox"
            disabled={!canManage || update.isPending}
            checked={enabled}
            onChange={(e) => update.mutate({ org_shared: e.target.checked })}
            className="accent-primary h-4 w-4"
          />
          <span>{enabled ? "Shared with organization" : "Private"}</span>
        </label>
      </div>

      {enabled && (
        <div className="rounded-md border border-border bg-surface-elevated/30 p-3 space-y-2">
          <div className="text-[11px] text-muted-foreground">Default access for org members</div>
          <div className="flex items-center gap-2">
            <select
              disabled={!canManage || update.isPending}
              value={defaultRole}
              onChange={(e) =>
                update.mutate({
                  org_shared_default_role: e.target.value as "viewer" | "analyst",
                })
              }
              className="bg-surface-elevated border border-border rounded-md px-2 py-1 text-[12px]"
            >
              <option value="viewer">Viewer (read-only, can chat)</option>
              <option value="analyst">Analyst (can edit graph + write via chat)</option>
            </select>
          </div>
          {defaultRole === "analyst" && (
            <p className="text-[11px] text-amber-300 mt-1">
              ⚠️ Analysts can modify workspace data and use AI actions that write to
              the graph. Use carefully org-wide.
            </p>
          )}
        </div>
      )}

      {!canManage && wsQ.data && (
        <p className="text-[11px] text-muted-foreground">
          Only the workspace owner or an organization admin can change sharing.
        </p>
      )}
      {error && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/5 p-2 text-[11px] text-rose-300">
          {error}
        </div>
      )}
    </div>
  );
}

function DangerTab({
  workspaceName,
  onDelete,
}: {
  workspaceName: string;
  onDelete: () => Promise<void>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matches = typed.trim() === workspaceName;

  const execute = async () => {
    if (!matches) return;
    setError(null);
    setBusy(true);
    try {
      await onDelete();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
      setBusy(false);
    }
  };

  return (
    <div className="panel p-5 border-risk-high/40">
      <Eyebrow>Danger Zone</Eyebrow>
      <p className="text-[12px] text-muted-foreground mb-3">
        Deleting a workspace wipes all entities, relationships, signals, and scenarios from the graph permanently. This cannot be undone.
      </p>
      {!confirming ? (
        <button
          onClick={() => setConfirming(true)}
          className="rounded-md border border-risk-high/40 bg-risk-high/15 text-risk-high px-4 py-2 text-[13px] font-medium hover:bg-risk-high/25"
        >
          Delete workspace
        </button>
      ) : (
        <div className="space-y-3">
          <p className="text-[12px] text-foreground/90">
            Type <strong>{workspaceName}</strong> to confirm deletion:
          </p>
          <input
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={workspaceName}
            className="w-full bg-surface-elevated border border-border rounded-md px-3 py-2 text-[13px] outline-none focus:border-risk-high/50 font-mono"
          />
          {error && (
            <p className="text-[12px] text-risk-high inline-flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> {error}
            </p>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => {
                setConfirming(false);
                setTyped("");
                setError(null);
              }}
              disabled={busy}
              className="rounded-md border border-border bg-surface px-4 py-2 text-[12px] text-foreground/80 hover:bg-surface-elevated"
            >
              Cancel
            </button>
            <button
              onClick={execute}
              disabled={!matches || busy}
              className="rounded-md bg-risk-high text-white px-4 py-2 text-[12px] font-semibold disabled:opacity-50"
            >
              {busy ? "Deleting…" : "I understand, delete workspace"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="label-eyebrow mb-1.5">{label}</div>
      {children}
    </div>
  );
}

// ---------- Devices tab: bridge tokens ----------

/** Lifetimes offered when minting a bridge token. The service enforces the
 *  real bounds; these are just the sane choices an operator actually wants.
 *  There is no "never" — a credential that lives on a phone in the field
 *  should always have an end date. */
const TTL_PRESETS: { hours: number; label: string }[] = [
  { hours: 24, label: "1 day" },
  { hours: 24 * 7, label: "7 days" },
  { hours: 24 * 30, label: "30 days" },
  { hours: 24 * 90, label: "90 days" },
  { hours: 24 * 365, label: "1 year" },
];

/**
 * QR of the provisioning payload, so the operator can configure the bridge
 * app by scanning instead of typing a 43-character token on a phone screen
 * outdoors. Rendered client-side into a canvas — the payload contains a live
 * credential, so it must never be sent to a third-party QR service.
 *
 * The manual copy path above stays: some devices have no camera, and some
 * operators are provisioning a bridge that isn't a phone at all.
 */
function ProvisioningQr({ provisioning }: { provisioning: ApiBridgeProvisioning }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [shown, setShown] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shown) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    let cancelled = false;
    // Imported lazily so the QR library isn't in the bundle for every user
    // who never opens Settings.
    import("qrcode")
      .then((QR) =>
        QR.toCanvas(canvas, JSON.stringify(provisioning), {
          width: 208,
          margin: 1,
          errorCorrectionLevel: "M",
        }),
      )
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not render QR");
      });
    return () => {
      cancelled = true;
    };
  }, [shown, provisioning]);

  return (
    <div className="mt-3">
      {!shown ? (
        <button
          onClick={() => setShown(true)}
          className="inline-flex items-center gap-1.5 rounded border border-border px-2 py-1.5 text-[11px] hover:bg-surface-elevated"
        >
          <QrCode className="h-3.5 w-3.5" />
          Show provisioning QR
        </button>
      ) : (
        <div>
          <div className="text-[11px] text-muted-foreground mb-1.5">
            Scan from the bridge app to configure it. Treat this like the token
            itself — anyone who photographs it can write to this workspace.
          </div>
          <div className="inline-block rounded bg-white p-2">
            <canvas ref={canvasRef} />
          </div>
          {error && <div className="mt-1 text-[11px] text-risk-high">{error}</div>}
          <div>
            <button
              onClick={() => setShown(false)}
              className="mt-1.5 text-[11px] text-muted-foreground hover:text-foreground"
            >
              Hide QR
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Bridge tokens are the credential a device bridge uses to talk to this
 * workspace. The raw value exists exactly once, in the create response —
 * the server stores only its SHA-256 — so the UI has to make that moment
 * obvious and hard to skip past.
 */
function DevicesTab({ workspaceId }: { workspaceId: string }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  // Holds the one-time secret after creation, until the admin dismisses it.
  const [issued, setIssued] = useState<ApiBridgeTokenCreated | null>(null);
  const [copied, setCopied] = useState(false);
  // Hard lifetime for the credential. Defaults to 30 days: long enough for a
  // deployment, short enough that a lost phone stops being useful.
  const [ttlHours, setTtlHours] = useState(24 * 30);
  const [confirmRevoke, setConfirmRevoke] = useState<ApiBridgeToken | null>(null);

  const tokensQ = useQuery({
    queryKey: ["bridge-tokens", workspaceId],
    queryFn: () => bridgeTokensApi.list(workspaceId),
  });

  const create = useMutation({
    mutationFn: () => bridgeTokensApi.create(workspaceId, name.trim(), ttlHours),
    onSuccess: (token) => {
      setIssued(token);
      setName("");
      qc.invalidateQueries({ queryKey: ["bridge-tokens", workspaceId] });
    },
  });

  const revoke = useMutation({
    mutationFn: (tokenId: string) => bridgeTokensApi.revoke(workspaceId, tokenId),
    onSuccess: () => {
      setConfirmRevoke(null);
      qc.invalidateQueries({ queryKey: ["bridge-tokens", workspaceId] });
    },
  });

  const tokens = tokensQ.data ?? [];
  const active = tokens.filter((t) => !t.revoked_at);
  const revoked = tokens.filter((t) => t.revoked_at);

  return (
    <div className="space-y-6">
      <section>
        <div className="label-eyebrow mb-1.5">Bridge tokens</div>
        <p className="text-[12px] text-muted-foreground mb-3 max-w-[620px]">
          A bridge runs on your network next to the hardware and relays device
          registration, telemetry and captures into this workspace. Each bridge
          authenticates with its own token, scoped to this workspace only.
        </p>

        <div className="flex gap-2 items-end">
          <div className="flex-1 max-w-[320px]">
            <div className="label-eyebrow mb-1.5">Name</div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Drone bridge — Facility A"
              className="w-full rounded border border-border bg-surface px-2.5 py-1.5 text-[12px] focus:outline-none focus:border-primary/50"
            />
          </div>
          <div>
            <div className="label-eyebrow mb-1.5">Expires after</div>
            <select
              value={ttlHours}
              onChange={(e) => setTtlHours(Number(e.target.value))}
              className="rounded border border-border bg-surface px-2.5 py-1.5 text-[12px] focus:outline-none focus:border-primary/50"
            >
              {TTL_PRESETS.map((p) => (
                <option key={p.hours} value={p.hours}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => create.mutate()}
            disabled={!name.trim() || create.isPending}
            className="inline-flex items-center gap-1.5 rounded bg-primary/15 px-3 py-1.5 text-[12px] font-medium text-primary hover:bg-primary/25 disabled:opacity-40"
          >
            {create.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Create token
          </button>
        </div>
        {create.isError && (
          <div className="mt-2 text-[11px] text-risk-high">
            {create.error instanceof Error ? create.error.message : "Could not create token"}
          </div>
        )}
      </section>

      {issued && (
        <section className="rounded-md border border-primary/40 bg-primary/5 p-3">
          <div className="flex items-start gap-2">
            <KeyRound className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold text-foreground mb-0.5">
                Copy this token now
              </div>
              <p className="text-[11px] text-muted-foreground mb-2">
                This is the only time it is shown. Horizon stores only a hash —
                if you lose it, revoke this token and create another.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate rounded bg-background px-2 py-1.5 font-mono text-[11px] text-foreground">
                  {issued.token}
                </code>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(issued.token);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1500);
                    } catch {
                      /* clipboard blocked — the value is selectable above */
                    }
                  }}
                  className="shrink-0 inline-flex items-center gap-1 rounded border border-border px-2 py-1.5 text-[11px] hover:bg-surface-elevated"
                >
                  <Copy className={`h-3 w-3 ${copied ? "text-primary" : ""}`} />
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              {issued.expires_at && (
                <div className="mt-1.5 text-[11px] text-muted-foreground">
                  Stops working{" "}
                  <span className="text-foreground/90">
                    {new Date(issued.expires_at).toLocaleString()}
                  </span>
                  . After that the bridge gets 401 until you issue a new token.
                </div>
              )}

              <ProvisioningQr provisioning={issued.provisioning} />

              <button
                onClick={() => setIssued(null)}
                className="mt-2 text-[11px] text-muted-foreground hover:text-foreground"
              >
                I've saved it — dismiss
              </button>
            </div>
          </div>
        </section>
      )}

      <section>
        <div className="label-eyebrow mb-1.5">
          Active {active.length > 0 && `(${active.length})`}
        </div>
        {tokensQ.isLoading ? (
          <div className="text-[12px] text-muted-foreground">Loading…</div>
        ) : active.length === 0 ? (
          <div className="text-[12px] text-muted-foreground italic">
            No active tokens. Create one to connect a bridge.
          </div>
        ) : (
          <div className="space-y-1.5">
            {active.map((t) => (
              <div
                key={t.id}
                className="flex items-center gap-3 rounded border border-border bg-surface/50 px-3 py-2"
              >
                <KeyRound
                  className={`h-3.5 w-3.5 shrink-0 ${t.expired ? "text-risk-high" : "text-muted-foreground"}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[12px] font-medium truncate">{t.name}</span>
                    {t.expired && (
                      <span className="shrink-0 rounded bg-risk-high/15 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-risk-high">
                        Expired
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    Created {new Date(t.created_at).toLocaleDateString()}
                    {" · "}
                    {t.last_used_at
                      ? `last used ${new Date(t.last_used_at).toLocaleString()}`
                      : "never used"}
                    {t.expires_at && (
                      <>
                        {" · "}
                        <span className={t.expired ? "text-risk-high" : ""}>
                          {t.expired ? "expired " : "expires "}
                          {new Date(t.expires_at).toLocaleDateString()}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setConfirmRevoke(t)}
                  className="shrink-0 text-[11px] text-muted-foreground hover:text-risk-high"
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {revoked.length > 0 && (
        <section>
          <div className="label-eyebrow mb-1.5">Revoked ({revoked.length})</div>
          <div className="space-y-1">
            {revoked.map((t) => (
              <div
                key={t.id}
                className="flex items-center gap-3 rounded border border-border/50 px-3 py-1.5 opacity-60"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] truncate line-through">{t.name}</div>
                </div>
                <span className="text-[10px] text-muted-foreground shrink-0">
                  revoked {new Date(t.revoked_at!).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {confirmRevoke && (
        <Modal onClose={() => setConfirmRevoke(null)} title="Revoke bridge token">
          <p className="text-[12px] text-muted-foreground mb-4">
            Any bridge using <span className="text-foreground">{confirmRevoke.name}</span>{" "}
            stops being able to reach this workspace immediately. Devices it
            registered stay in the graph, but they will go offline until a
            bridge reconnects with a valid token. This cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setConfirmRevoke(null)}
              className="rounded border border-border px-3 py-1.5 text-[12px] hover:bg-surface-elevated"
            >
              Cancel
            </button>
            <button
              onClick={() => revoke.mutate(confirmRevoke.id)}
              disabled={revoke.isPending}
              className="inline-flex items-center gap-1.5 rounded bg-risk-high/15 px-3 py-1.5 text-[12px] font-medium text-risk-high hover:bg-risk-high/25 disabled:opacity-40"
            >
              {revoke.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Revoke token
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
